import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Loader2, Plus, FileText, CheckCircle2, MoreVertical, Archive, ArchiveRestore, Trash2, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  listSurveys,
  upsertSurvey,
  setActiveSurvey,
  setSurveyStatus,
  deleteSurvey,
  getSurveyDataCounts,
  type AdminSurveyRow,
  type SurveyDataCounts,
} from '@/lib/admin/surveys-api';

export default function AdminSurveysList() {
  const { orgId } = useOutletContext<{ orgId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();

  const [deleteTarget, setDeleteTarget] = useState<AdminSurveyRow | null>(null);
  const [deleteCounts, setDeleteCounts] = useState<SurveyDataCounts | null>(null);

  const q = useQuery({
    queryKey: ['admin', 'surveys', orgId],
    queryFn: () => listSurveys(orgId),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'surveys', orgId] });

  const createMut = useMutation({
    mutationFn: () => upsertSurvey({ pulseOrgId: orgId, name: 'Untitled survey' }),
    onSuccess: (id) => navigate(`/app/admin/surveys/${id}`),
    onError: (e: Error) => toast({ title: 'Could not create survey', description: e.message, variant: 'destructive' }),
  });

  const activateMut = useMutation({
    mutationFn: (surveyId: string | null) => setActiveSurvey(orgId, surveyId),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Active survey updated' });
    },
    onError: (e: Error) => toast({ title: 'Activation failed', description: e.message, variant: 'destructive' }),
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'draft' | 'archived' }) => setSurveyStatus(id, status),
    onSuccess: (_d, vars) => {
      invalidate();
      toast({ title: vars.status === 'archived' ? 'Survey archived' : 'Survey restored' });
    },
    onError: (e: Error) => toast({ title: 'Status change failed', description: e.message, variant: 'destructive' }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteSurvey(id),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Survey deleted' });
      setDeleteTarget(null);
      setDeleteCounts(null);
    },
    onError: (e: Error) => toast({ title: 'Delete failed', description: e.message, variant: 'destructive' }),
  });

  const openDelete = async (s: AdminSurveyRow) => {
    setDeleteTarget(s);
    setDeleteCounts(null);
    try {
      const counts = await getSurveyDataCounts(s.id);
      setDeleteCounts(counts);
    } catch (e) {
      toast({ title: 'Could not load survey data', description: (e as Error).message, variant: 'destructive' });
    }
  };

  if (q.isLoading) return <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />;
  if (q.error) return <p className="text-sm text-destructive">{(q.error as Error).message}</p>;

  const surveys = q.data ?? [];
  const hasData = (deleteCounts?.anchor_responses ?? 0) > 0;

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-semibold">Surveys & pulse topics</h2>
          <p className="text-sm text-muted-foreground">
            Define what Charles asks about. The active survey is sent to the agent as topic + anchor briefs.
          </p>
        </div>
        <Button onClick={() => createMut.mutate()} disabled={createMut.isPending}>
          {createMut.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
          New survey
        </Button>
      </div>

      {surveys.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            <FileText className="h-8 w-8 mx-auto mb-3 opacity-50" />
            No surveys yet. Create one to define the topic Charles asks employees about.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {surveys.map((s) => {
            const isArchived = s.status === 'archived';
            return (
              <Card key={s.id} className={isArchived ? 'opacity-60' : 'hover:bg-muted/40 transition-colors'}>
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <button onClick={() => navigate(`/app/admin/surveys/${s.id}`)} className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{s.name}</span>
                      {s.is_active && (
                        <Badge variant="default" className="gap-1">
                          <CheckCircle2 className="h-3 w-3" /> Active
                        </Badge>
                      )}
                      <Badge variant="outline" className="capitalize">{s.status}</Badge>
                    </div>
                    {s.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{s.description}</p>
                    )}
                  </button>

                  <div className="flex items-center gap-2 shrink-0">
                    {!isArchived && (s.is_active ? (
                      <Button size="sm" variant="outline" onClick={() => activateMut.mutate(null)} disabled={activateMut.isPending}>
                        Deactivate
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => activateMut.mutate(s.id)} disabled={activateMut.isPending}>
                        Set active
                      </Button>
                    ))}

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="icon" variant="ghost" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {isArchived ? (
                          <DropdownMenuItem onClick={() => statusMut.mutate({ id: s.id, status: 'draft' })}>
                            <ArchiveRestore className="h-4 w-4 mr-2" /> Restore to draft
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => statusMut.mutate({ id: s.id, status: 'archived' })}>
                            <Archive className="h-4 w-4 mr-2" /> Archive
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => openDelete(s)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" /> Delete permanently…
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => {
          if (!o) {
            setDeleteTarget(null);
            setDeleteCounts(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete "{deleteTarget?.name}" permanently?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>This cannot be undone. The survey and everything tied to it will be erased.</p>
                {deleteCounts ? (
                  <div className="rounded-md border border-border bg-muted/40 p-3 space-y-1">
                    <div className="flex justify-between"><span>Anchors</span><span className="font-medium tabular-nums">{deleteCounts.anchors}</span></div>
                    <div className="flex justify-between"><span>Employee responses</span><span className="font-medium tabular-nums">{deleteCounts.anchor_responses}</span></div>
                    {deleteCounts.is_active && (
                      <div className="text-destructive font-medium pt-1">This survey is currently active — deleting will deactivate it.</div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Checking what's attached…</div>
                )}
                {hasData && (
                  <p className="text-destructive font-medium">
                    Warning: {deleteCounts!.anchor_responses} employee response{deleteCounts!.anchor_responses === 1 ? '' : 's'} will be permanently destroyed. Consider archiving instead.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={!deleteCounts || deleteMut.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (deleteTarget) deleteMut.mutate(deleteTarget.id);
              }}
            >
              {deleteMut.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Delete forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
